import {CommonModule} from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {NzToolTipModule} from 'ng-zorro-antd/tooltip';

import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {URL_PARAMS} from '@constant/urlparams-keys';
import {IntlTelInputDirective} from '@directives/intlTelInput.directive';
import {
  BknPaymentForm,
  BknPaymentFormControls,
  PaymentDto,
  SubmerchantDto,
} from '@models/index';
import {InputMaskModule, createMask} from '@ngneat/input-mask';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TranslateModule} from '@ngx-translate/core';
import {CardNumberStylePipe} from '@pipes/card-number-style.pipe';
import {CardTypePipe} from '@pipes/card-type.pipe';
import {CheckCCNPipe} from '@pipes/check-ccn.pipe';
import {SettingsService} from '@services/settings.service';
import {PaymentFacade} from '@store/index';
import {
  CVVunipayValidation,
  cardTestValidator,
  luhnValidator,
} from '@utils/index';
import {paramsToLower} from '@utils/query-params-utils';
import {NzCheckboxModule} from 'ng-zorro-antd/checkbox';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzSpinComponent} from 'ng-zorro-antd/spin';
import {Observable, filter, map} from 'rxjs';
import {BtnSubmerchantPayComponent} from '../../layouts/btn-submerchant-pay/btn-submerchant-pay.component';
@UntilDestroy()
@Component({
  selector: 'app-bkn-payment-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NzFormModule,
    NzInputModule,
    InputMaskModule,
    CardNumberStylePipe,
    CheckCCNPipe,
    CardTypePipe,
    BtnSubmerchantPayComponent,
    NzToolTipModule,
    NzSpinComponent,
    NzCheckboxModule,
    FormsModule,
    IntlTelInputDirective,
  ],
  templateUrl: './bkn-payment-form.component.html',
  styleUrl: './bkn-payment-form.component.scss',
})
export class BknPaymentFormComponent {
  _activatedRouter = inject(ActivatedRoute);
  _paymentFacade = inject(PaymentFacade);
  @Input() subMerchant?: SubmerchantDto | null | undefined;
  @Input() payment?: PaymentDto | null | undefined;
  @Input() isSpinning?: boolean | null = false;
  @Input() isChangingPaymentMethod?: boolean = false;
  @Input() embeded: boolean = false;
  @Input() embedTheme: 'light' | 'dark' = 'light';
  errorMessage: string = '';
  isPaying: boolean = false;

  @Input() collectEmail: boolean = false;
  @Input() collectPhoneNumber: boolean = false;

  paymentForm: FormGroup<BknPaymentFormControls>;

  cards$: Observable<string[]>;

  showEmailField: boolean = true;
  showPhoneField: boolean = false;

  isOccupied = {
    cardHolderName: false,
    creditCardNumber: false,
    expiredMonth: false,
    expiredYear: false,
    securityCode: false,
    subscriberEmail: false,
    subscriberMobile: false,
  };

  _settingService = inject(SettingsService);

  @Output() submitEvent: EventEmitter<BknPaymentForm> = new EventEmitter();

  @ViewChild('ccnumber') ccnumberElement?: ElementRef;
  @ViewChild('fullname') fullnameElement?: ElementRef;
  @ViewChild('month') monthElement?: ElementRef;
  @ViewChild('year') yearElement?: ElementRef;
  @ViewChild('cvv') cvvElement?: ElementRef;

  @ViewChild('subscriberEmail') subscriberEmail?: ElementRef;
  @ViewChild('subscriberMobile') subscriberMobile?: ElementRef;

  onlyNumbersMask = createMask<string>({
    regex: '^[0-9 ]+$',
    placeholder: '',
  });
  phonePattern: RegExp = /^\+?[1-9]\d{6,14}$/;
  phonePatternString: string = '^\\+?[1-9]\\d{6,14}$';

  darkBackgroundColor: string = '#000000';
  lightBackgroundColor: string = '#ffffff';
  embedRequired: boolean = false;

  constructor(private fb: FormBuilder) {
    this.showEmailField = this.collectEmail;
    this.showPhoneField = this.payment?.collectNumber || false;

    // Define your base form structure first
    const formGroup: any = {
      cardHolderName: new FormControl<string | null>('', {
        validators: [
          Validators.pattern('^[A-Za-zñÑáéèíóúÁÉÍÓÚ ]+$'),
          Validators.maxLength(40),
        ],
      }),
      creditCardNumber: new FormControl<string | null>('', {
        validators: [
          Validators.required,
          Validators.minLength(13),
          Validators.maxLength(24),
          luhnValidator(),
          cardTestValidator(),
        ],
      }),
      securityCode: new FormControl<string | null>('', {
        validators: [
          Validators.pattern('[0-9]{3,4}'),
          Validators.minLength(2),
          Validators.maxLength(4),
        ],
      }),
      expirationDate: new FormGroup(
        {
          expiredMonth: new FormControl<string | null>('', {
            validators: [
              Validators.required,
              Validators.minLength(2),
              Validators.maxLength(2),
              Validators.max(12),
            ],
          }),
          expiredYear: new FormControl<string | null>('', {
            validators: [
              Validators.minLength(2),
              Validators.maxLength(2),
              Validators.required,
              Validators.min(new Date().getFullYear() - 2000),
            ],
          }),
        },
        [Validators.required, Validators.minLength(2), Validators.maxLength(2)]
      ),
      subscriberEmail: new FormControl<string | null>('', {
        validators: [Validators.email],
      }),
      subscriberMobile: new FormControl<string | null>('', {
        validators: [],
      }),
      fullName: new FormControl<string | null>('', {
        validators: [],
      }),
      showSubscriberEmail: new FormControl<boolean | null>(false),
    };

    // Conditionally add email and phone number form controls if flags are true
    if (this.showEmailField) {
      formGroup.emailAddress = new FormControl<string | null>('', {
        validators: [Validators.email],
      });
    }

    if (this.showPhoneField) {
      formGroup.mobileNumber = new FormControl<string | null>('', {
        validators: [Validators.pattern(this.phonePattern)],
      });
    }

    // Create the form group with the dynamic fields
    this.paymentForm = this.fb.group<BknPaymentFormControls>(formGroup, {
      validators: [CVVunipayValidation()],
    });

    // Set up the cards observable
    this.cards$ = this._settingService
      .Get()
      .pipe(
        map(xx =>
          xx.cards
            ? xx.cards.map(xx => `../../../../../assets/images/${xx.path}`)
            : []
        )
      );

    // Focus handling for expiration year, month, and credit card number
    this.paymentForm
      .get('expiredYear')
      ?.valueChanges.pipe(
        filter(
          // @ts-ignore
          (value: string | null) => value?.replaceAll(' ', '').length >= 2
        )
      )
      .subscribe(() => this.cvvElement?.nativeElement.focus());

    this.paymentForm
      .get('expiredMonth')
      ?.valueChanges.pipe(
        filter(
          // @ts-ignore
          (value: string | null) => value?.replaceAll(' ', '').length >= 2
        )
      )
      .subscribe(() => this.yearElement?.nativeElement.focus());

    this.paymentForm
      .get('creditCardNumber')
      ?.valueChanges.pipe(
        filter(
          // @ts-ignore
          (value: string | null) => value?.replaceAll(' ', '').length >= 19
        )
      )
      .subscribe(() => this.monthElement?.nativeElement.focus());
  }

  setfocus(isfocus: boolean, key: string) {
    switch (key) {
      case 'creditCardNumber':
        this.isOccupied.creditCardNumber = isfocus;
        break;
      case 'cardHolderName':
        this.isOccupied.cardHolderName = isfocus;
        break;
      case 'expiredMonth':
        this.isOccupied.expiredMonth = isfocus;
        break;
      case 'expiredYear':
        this.isOccupied.expiredYear = isfocus;
        break;
      case 'securityCode':
        this.isOccupied.securityCode = isfocus;
        break;
      case 'subscriberEmail':
        this.isOccupied.subscriberEmail = isfocus;
        break;
      case 'subscriberMobile':
        this.isOccupied.subscriberMobile = isfocus;
        break;
      default:
        break;
    }
  }

  get mobileNumber() {
    return this.paymentForm.get('mobileNumber')!;
  }

  onPastecheckCC(ev: string | null | undefined) {
    if (!ev) return;

    if (!/^\s*\d[\d\s]*$/.test(ev)) {
      ev = ev?.replace(/\D/g, '');
    }

    ev = ev
      .replace(/[^\dA-Z]/g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim();

    this.paymentForm.controls.creditCardNumber.setValue(ev);

    if (ev.replaceAll(' ', '').length >= 19) {
      this.monthElement?.nativeElement.focus();
    }
  }

  onAutoFillcheckCC(event: any | null | undefined) {
    if (!event) return;

    setTimeout(() => {
      var ev = event.target?.value;

      if (!ev) return;

      if (!/^\s*\d[\d\s]*$/.test(ev)) {
        ev = ev?.replace(/\D/g, '');
      }

      ev = ev
        .replace(/[^\dA-Z]/g, '')
        .replace(/(.{4})/g, '$1 ')
        .trim();

      this.paymentForm.controls.creditCardNumber.setValue(ev);

      if (ev.replaceAll(' ', '').length >= 19) {
        // this.isOccupied.cardHolderName
        //   ? this.ccnumberElement?.nativeElement.focus()
        //   : this.monthElement?.nativeElement.focus();
      }
    }, 500);
  }

  onAutoFillcheckCH(event: any | null | undefined) {
    if (!event) return;

    var ev = event.target?.value;

    if (!ev) return;

    ev = ev.trim();

    this.paymentForm.controls.cardHolderName.setValue(ev);

    if (ev.length >= 40) {
      this.ccnumberElement?.nativeElement.focus();
    }
  }

  onAutoFillEventMonth(event: any | null | undefined) {
    if (!event) return;

    setTimeout(() => {
      var ev = event.target?.value;

      if (!ev) return;

      if (!/^\s*\d[\d\s]*$/.test(ev)) {
        ev = ev?.replace(/\D/g, '');
      }

      ev = ev.trim();

      // this.paymentForm.controls.expiredMonth.setValue(ev);
      this.paymentForm.patchValue({
        expirationDate: {
          expiredMonth: ev,
        },
      });

      if (ev.length >= 2) {
        this.yearElement?.nativeElement.focus();
      }
    }, 500);
  }

  onAutoFillEventYear(event: any | null | undefined) {
    if (!event) return;

    setTimeout(() => {
      var ev = event.target?.value;

      if (!ev) return;

      if (!/^\s*\d[\d\s]*$/.test(ev)) {
        ev = ev?.replace(/\D/g, '');
      }

      ev = ev.trim();

      // this.paymentForm.controls.expiredYear.setValue(ev);
      this.paymentForm.patchValue({
        expirationDate: {
          expiredYear: ev,
        },
      });

      if (ev.length >= 2) {
        this.cvvElement?.nativeElement.focus();
      }
    }, 500);
  }

  onAutoFillEventCvv(event: any | null | undefined) {
    if (!event) return;

    setTimeout(() => {
      var ev = event.target?.value;

      if (!ev) return;

      if (!/^\s*\d[\d\s]*$/.test(ev)) {
        ev = ev?.replace(/\D/g, '');
      }

      ev = ev.trim();

      this.paymentForm.controls.securityCode.setValue(ev);

      if (ev.length >= 4) {
        this.subscriberEmail?.nativeElement.focus();
      }
    }, 500);
  }

  pay() {
    var holderName = this.paymentForm.controls?.cardHolderName?.value;
    this.paymentForm.controls.cardHolderName.setValue(
      holderName ? holderName.toUpperCase() : null
    );

    var subscriberemail = this.paymentForm.controls?.subscriberEmail?.value;
    var subscribermobile = this.paymentForm.controls?.subscriberMobile?.value;

    // if ((subscriberemail || subscribermobile) && this.paymentForm.value) {
    //   this.paymentForm.value.subscriber = {
    //     emailAddress: subscriberemail?.toUpperCase(),
    //     mobileNumber: subscribermobile?.toUpperCase(),
    //   } as ISubscriberData;
    // }

    if (subscriberemail && this.paymentForm.value) {
      this.paymentForm.value.emailAddress = subscriberemail?.toUpperCase();
    }

    const emailControl = this.paymentForm.get('emailAddress');
    const phoneControl = this.paymentForm.get('mobileNumber');

    if (emailControl) {
      this.paymentForm.value.emailAddress = emailControl.value?.toUpperCase();
    }

    if (phoneControl) {
      this.paymentForm.value.mobileNumber = phoneControl.value;
    }

    const subscriberEmailControl = this.paymentForm.get('subscriberEmail');
    const subscriberMobileControl = this.paymentForm.get('mobileNumber');

    const subscriber = {
      emailAddress: subscriberEmailControl
        ? subscriberEmailControl.value || ''
        : '',
      mobileNumber: subscriberMobileControl
        ? subscriberMobileControl.value || ''
        : '',
    };
    // this following snippet gets the parameter embedRequired from the URL query parameters
    this._activatedRouter.root.queryParams
      .pipe(untilDestroyed(this))
      .subscribe(item => {
        const res = paramsToLower(item);
        this.embedRequired = this.toBoolean(res?.[URL_PARAMS.EMBEDREQUIRED]);
      });

    if (this.paymentForm.valid) {
      /* Set loading state to false in case of embedded mode.
      As per the request from plugin agency, we need to hide the loading in case the paywall is embedded
      and directly notify the parent that the payment is submitted */
      this.isPaying = !this.embedRequired;
      this.submitEvent.emit({
        ...this.paymentForm.value,
        cardHolderName: this.paymentForm.controls.cardHolderName.value || '',
        creditCardNumber:
          this.paymentForm.controls.creditCardNumber.value || '',
        securityCode: this.paymentForm.controls.securityCode.value || '',
        expiredMonth:
          this.paymentForm.get('expirationDate')?.value.expiredMonth || '',
        expiredYear:
          this.paymentForm.get('expirationDate')?.value.expiredYear || '',
        subscriberEmail: this.paymentForm.controls.subscriberEmail.value || '',
        subscriberMobile:
          this.paymentForm.controls.subscriberMobile.value || '',
        showSubscriberEmail:
          this.paymentForm.controls.showSubscriberEmail.value || false,
        subscriber: subscriber,
        payerEmail: this.paymentForm.controls.emailAddress
          ? this.paymentForm.controls.emailAddress.value || ''
          : '',
        payerMobile: this.paymentForm.controls.mobileNumber
          ? this.paymentForm.controls.mobileNumber.value || ''
          : '',
      });
    } else {
      this.paymentForm.markAllAsTouched();
    }
  }

  // Utility function to check if a parameter (usually string) is of true value
  toBoolean(param?: unknown): boolean {
    return param?.toString().toLowerCase() === 'true';
  }
}
